// Event badges ("Few seats left", "Trending", "This weekend"…). Same rules as the app's
// eventHighlights (lib/pages/event_app/widgets/event_ui.dart).

// Calendar day of a stored timestamp, as stored (event-local), not shifted into the viewer's zone.
const dayOf = (iso) => {
  const [y, m, d] = String(iso || "").slice(0, 10).split("-").map(Number);
  return y ? new Date(y, m - 1, d) : null;
};
const daysFromToday = (iso) => {
  const day = dayOf(iso);
  if (!day) return null;
  const now = new Date();
  return Math.round((day - new Date(now.getFullYear(), now.getMonth(), now.getDate())) / 86400000);
};

/** People who can still get in (a group pass admits group-size people); null if unknown. */
export const seatsLeft = (event) => {
  const passes = (event.tickets || []).filter((t) => t.is_active && t.ticket_name?.toLowerCase() !== "host");
  if (!passes.length || passes.some((t) => t.remaining_tickets == null)) return null;
  return passes.reduce((n, t) => n + Math.max(0, t.remaining_tickets) * Math.max(1, t.group_size || 1), 0);
};

/** Up to `max` badges, most urgent first: { label, tone } with tone hot | warm | soon | muted | elite. */
export function eventHighlights(event, max = 2) {
  const out = [];
  // Same test as the event card's "Registration closed".
  const closed = event.rsvp_paused || (event.last_date && new Date(event.last_date).getTime() < Date.now());
  const open = !closed;
  const capacity = event.total_seats || 0;
  const left = seatsLeft(event);

  if (open && left !== null) {
    if (left === 0) out.push({ label: "Sold out · Waitlist", tone: "muted" });
    else if (left <= 5) out.push({ label: `Only ${left} left`, tone: "hot" });
    else if (capacity > 0 && left <= Math.ceil(capacity * 0.15)) out.push({ label: "Few seats left", tone: "hot" });
  }
  const recent = event.recent_activity || 0;
  if (open && recent >= 5 && recent >= capacity * 0.1) out.push({ label: "🔥 Trending", tone: "warm" });
  else if (open && !out.length && capacity > 0 && (event.total_registered || 0) >= capacity * 0.6) out.push({ label: "⚡ Filling fast", tone: "warm" });

  if (open) {
    const deadline = daysFromToday(event.last_date);
    if (deadline === 0) out.push({ label: "Last day to RSVP", tone: "hot" });
    if (deadline === 1) out.push({ label: "RSVP closes tomorrow", tone: "warm" });
  }
  if (!event.date_tbd) {
    const days = daysFromToday(event.event_start_date);
    const start = dayOf(event.event_start_date);
    const today = new Date().getDay(); // 0 Sun … 6 Sat
    if (days === 0) out.push({ label: "Today", tone: "soon" });
    else if (days === 1) out.push({ label: "Tomorrow", tone: "soon" });
    else if (days > 1 && days <= 6 && start && (start.getDay() === 6 || start.getDay() === 0) && today >= 1 && today <= 5) out.push({ label: "This weekend", tone: "soon" });
  }
  if (event.elite_only) out.push({ label: "Elite only", tone: "elite" });
  return out.slice(0, max);
}
